/* The aggregation across everything with a date on it — handover §14 step 8 calls
   this "the payoff", and it is: eight registers each holding a date nobody looks
   at, turned into one list ordered by how soon it hurts.

   Pure. Takes already-fetched collections and returns computed rows, so it works
   identically against mock data, live Graph data or the IndexedDB cache with no
   network at all. Nothing here is stored — recompute it, never persist it. */

import type {
  IApplication,
  IArticle,
  ICredential,
  ICustomRecord,
  IDevice,
  IDomain,
  IExpiry,
  IPerson,
  ILicenceSku,
  IRecordType,
  ISupplier,
} from '../models';
import { daysUntil } from './format';
import { REVIEW_DAYS } from '../services/mockData';
import { isLeaver, retentionDue } from './registers';

export interface ExpiryInputs {
  devices?: IDevice[];
  people?: IPerson[];
  suppliers?: ISupplier[];
  domains?: IDomain[];
  applications?: IApplication[];
  credentials?: ICredential[];
  articles?: IArticle[];
  skus?: ILicenceSku[];
  recordTypes?: IRecordType[];
  customRecords?: ICustomRecord[];
}

/* UTC throughout, and it has to be.
 *
 * A plain date string parses as UTC midnight, setDate/getDate work in local
 * time, and toISOString reads back in UTC. Mixing the three loses a day
 * whenever the arithmetic crosses the March or October changeover: a contract
 * ending 2027-01-07 with ninety days notice came out as 2026-10-08, because the
 * answer lands in British Summer Time and the input did not.
 *
 * Every notice deadline, credential rotation and runbook review computed across
 * a changeover was a day early. The same mistake, in the same shape, as the one
 * that had the sync rewriting eighty-five rows a night — see sync/src/dates.ts,
 * which is where the reasoning is written out in full. */
function addDays(iso: string, n: number): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

/** Everything with a date, flattened and sorted soonest-first. */
export function buildExpiries(input: ExpiryInputs): IExpiry[] {
  const out: IExpiry[] = [];
  const push = (e: Omit<IExpiry, 'days'>) => {
    const days = daysUntil(e.due);
    if (!Number.isFinite(days)) return;
    out.push({ ...e, days });
  };

  for (const d of input.devices ?? []) {
    /* Owned or hired, never both. A hired machine has no warranty — the
       contract is the warranty — and a purchased one has no contract, so
       pushing both would put a phantom date on every device either way. */
    if (d.ownership === 'Hired') {
      push({
        id: `hire-${d.id}`,
        label: `${d.tag} — hire contract`,
        detail: [d.hireSupplier, d.hireRef].filter(Boolean).join(' · ') || `${d.make} ${d.model}`,
        source: 'Hire',
        due: d.hireEnds ?? '',
        loc: d.loc,
        icon: 'ti-file-description',
        href: `/devices/${d.tag}`,
      });
      continue;
    }

    push({
      id: `warranty-${d.id}`,
      label: `${d.tag} — warranty`,
      detail: `${d.make} ${d.model}`,
      source: 'Warranty',
      due: d.warranty,
      loc: d.loc,
      icon: 'ti-device-laptop',
      href: `/devices/${d.tag}`,
    });
  }

  /* A leaver's retention date. This is the only entry here that ends in a
     deletion rather than a renewal, so it reads as an instruction — the point
     is that the account is still there, still costing and still able to hold
     access, until somebody removes it. */
  for (const p of input.people ?? []) {
    // Not every blocked account is a leaver — see isLeaver.
    if (!isLeaver(p)) continue;
    push({
      id: `retention-${p.id}`,
      label: `${p.name} — account retention ends`,
      detail: p.leftOn ? `Left ${p.leftOn}` : 'Leaving date not recorded',
      source: 'Leaver',
      due: retentionDue(p),
      loc: p.loc,
      icon: 'ti-user-off',
      href: '/people',
    });
  }

  for (const s of input.suppliers ?? []) {
    push({
      id: `contract-${s.id}`,
      label: `${s.name} — contract`,
      detail: s.notice === 'None' ? s.service : `${s.service} · ${s.notice} notice`,
      source: 'Contract',
      due: s.end,
      loc: null,
      icon: 'ti-building-store',
      href: `/suppliers/${s.id}`,
    });

    /* The notice date is the one that actually matters. Miss it and the contract
       has already rolled over — the end date is too late to be useful. */
    const noticeDays = parseInt(s.notice, 10);
    if (Number.isFinite(noticeDays) && noticeDays > 0) {
      push({
        id: `notice-${s.id}`,
        label: `${s.name} — notice deadline`,
        detail: `Last day to give ${s.notice} notice on ${s.service}`,
        source: 'Contract',
        due: addDays(s.end, -noticeDays),
        loc: null,
        icon: 'ti-alarm',
        href: `/suppliers/${s.id}`,
      });
    }
  }

  for (const d of input.domains ?? []) {
    push({
      id: `domain-${d.id}`,
      label: `${d.name} — registration`,
      detail: d.reg,
      source: 'Domain',
      due: d.regEnds,
      loc: null,
      icon: 'ti-world',
      href: `/domains/${d.id}`,
    });
    push({
      id: `ssl-${d.id}`,
      label: `${d.name} — certificate`,
      detail: d.ssl,
      source: 'SSL',
      due: d.sslEnds,
      loc: null,
      icon: 'ti-certificate',
      href: `/domains/${d.id}`,
    });
  }

  for (const a of input.applications ?? []) {
    push({
      id: `app-${a.id}`,
      label: `${a.name} — renewal`,
      detail: a.supplier,
      source: 'Application',
      due: a.renews,
      loc: null,
      icon: 'ti-apps',
      href: `/applications/${a.id}`,
    });
  }

  for (const sku of input.skus ?? []) {
    push({
      id: `sku-${sku.id}`,
      label: `${sku.name} — term`,
      detail: `${sku.total} prepaid units`,
      source: 'Licence',
      due: sku.renews,
      loc: null,
      icon: 'ti-license',
      href: '/licences',
    });
  }

  for (const c of input.credentials ?? []) {
    push({
      id: `rot-${c.id}`,
      label: `${c.name} — rotation`,
      detail: `Every ${c.every} days · ${c.system}`,
      source: 'Rotation',
      due: addDays(c.rotated, c.every),
      loc: null,
      icon: 'ti-key',
      href: `/credentials/${c.id}`,
    });
  }

  /* Article reviews. One of the two mechanisms that fight the thing that will
     actually kill this system (§17), so it belongs in the same list as a
     warranty rather than tucked away in the knowledge base. */
  for (const a of input.articles ?? []) {
    push({
      id: `review-${a.id}`,
      label: `${a.title} — review due`,
      detail: `Last updated by ${a.by}`,
      source: 'Review',
      due: addDays(a.upd, REVIEW_DAYS),
      loc: null,
      icon: 'ti-book-2',
      href: `/knowledge-base/${a.id}`,
    });
  }

  /* Custom records. Reads the promoted ExpiryDate column, not the payload —
     which is exactly why the promotion exists (§6.2). */
  const typeById = new Map((input.recordTypes ?? []).map((t) => [t.id, t]));
  for (const r of input.customRecords ?? []) {
    if (!r.expiryDate) continue;
    const type = typeById.get(r.typeId);
    push({
      id: `custom-${r.id}`,
      label: r.title,
      detail: type?.title ?? 'Custom record',
      source: 'Custom',
      due: r.expiryDate,
      loc: r.depot ?? null,
      icon: type?.icon ?? 'ti-file-text',
      href: `/records/${r.typeId}/${r.id}`,
    });
  }

  return out.sort((a, b) => a.days - b.days);
}

/** Everything due inside `within` days, including anything already overdue. */
export function dueWithin(expiries: IExpiry[], within: number): IExpiry[] {
  return expiries.filter((e) => e.days <= within);
}
